'use client'

import React from 'react'
import { PageFrame } from '../ui'
import { Send, Mail, Upload, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from '../ProcessedEntry/DomainEntry/processedTypes'
import { EmailPillInput } from '../EmailPillInput'

type PoolImage = { id: string; label: string; base64: string; mime: string }
const THUMB_W = 88
const THUMB_H = 116

// Generalized, ad-hoc version of the SIBS dispatch composer (SibsWidgets.tsx's
// SibsEmailWidget) — same outbound relay (Resend via TF_MAIL_SERVICE_KEY/
// TF_MAIL_SERVICE_SENDER, see backend's POST /campaigns/send-alert), but not
// tied to a specific entry: any Editor can type a free-form to/subject/body
// and dispatch it.
//
// A send from here IS logged now: /campaigns/send-alert writes the email_logs
// row (so it shows in the Email Logs view, the sibling tab under this same
// Administration > Email submenu) and records the send as an action taken on
// the alert_email channel, REF-tagged like every other tracked send so a
// reply comes back to it. Attaching it to an entry is optional — with a
// user_entry_id the action lands on that entry's "Actions taken" widget;
// without one it is a loose alert_email in Administration › Actions.
//
// Image attachments reuse the same "pick images as evidence" idea as the CTT
// brand campaign composer / DomainTakedownWidget, trimmed to manual upload
// only — there's no entry or screenshot history to pull from here since this
// composer isn't scoped to any particular entry.
export function EmailComposeView({ theme }: { theme: ProcessedTheme }) {
  const c = watchClasses(theme)
  const isDark = theme === 'dark'

  const [to, setTo] = React.useState<string[]>([])
  const [userEntryId, setUserEntryId] = React.useState('')
  const [subject, setSubject] = React.useState('')
  const [body, setBody] = React.useState('')
  const [images, setImages] = React.useState<PoolImage[]>([])
  const [sending, setSending] = React.useState(false)
  const [result, setResult] = React.useState<{ ok: boolean; msg: string } | null>(null)

  const isValid = to.length > 0 && subject.trim().length > 0 && body.trim().length > 0

  const readFile = (file: File) => {
    const reader = new FileReader()
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string
      const [header, base64] = dataUrl.split(',')
      const mime = header.match(/data:([^;]+)/)?.[1] ?? 'image/png'
      setImages((prev) => [...prev, { id: `upload-${Date.now()}-${prev.length}`, base64, mime, label: file.name || 'Image' }])
    }
    reader.readAsDataURL(file)
  }

  const send = async () => {
    if (!isValid || sending) return
    setSending(true)
    setResult(null)
    try {
      const res = await fetch('/api/campaigns/send-alert', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          to: to.join(','),
          subject,
          body,
          userEntryId: userEntryId.trim() || undefined,
          images: images.map((img) => ({ base64: img.base64, mime: img.mime, label: img.label })),
        }),
      })
      const payload = await res.json().catch(() => null)
      if (payload?.success) {
        setResult({ ok: true, msg: 'Email sent successfully.' })
        setSubject('')
        setBody('')
        setImages([])
      } else {
        setResult({ ok: false, msg: payload?.error ?? 'Send failed.' })
      }
    } catch {
      setResult({ ok: false, msg: 'Network error.' })
    } finally {
      setSending(false)
    }
  }

  return (
    <PageFrame padded={false} fill eyebrow="Administration · Email" title="Compose Email">
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-hidden p-5">
      <div className={`flex max-w-2xl flex-col gap-3 border p-4 ${c.panel} ${c.line}`}>
        <div className={`flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>
          <Mail className="h-3 w-3 shrink-0" />
          Manual sender · same outbound relay as SIBS alerts
        </div>

        <label className="flex flex-col gap-0.5">
          <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>To</span>
          <EmailPillInput value={to} onChange={setTo} theme={theme} placeholder="recipient@example.com" />
        </label>

        {/* Optional: the entry this email is about, so the send is filed
            against it as an action taken instead of standing alone. */}
        <label className="flex flex-col gap-0.5">
          <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Attach to entry (user_entry_id) <span className={'text-subtle'}>(optional)</span></span>
          <Input
            value={userEntryId}
            onChange={(e) => setUserEntryId(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
            className={`h-8 font-mono text-[11px] px-2 ${c.input}`}
          />
        </label>

        <label className="flex flex-col gap-0.5">
          <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Subject</span>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className={`h-8 text-[12px] px-2 ${c.input}`}
          />
        </label>

        <label className="flex flex-col gap-0.5">
          <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Body</span>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={10}
            className={`text-[12px] px-2 py-1.5 ${c.input}`}
          />
        </label>

        {/* Image attachments — see the file header comment for how this
            relates to CampaignForm.tsx's evidence gallery. */}
        <div className={`rounded border p-2 ${c.line}`}>
          <div className={`font-mono text-[9px] uppercase tracking-widest mb-1.5 ${c.faint}`}>
            Images <span className={'text-subtle'}>(optional)</span>
            {images.length > 0 && <> · <span className="text-signal">{images.length} attached</span></>}
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {images.map((img) => (
              <div key={img.id} className="relative shrink-0 overflow-hidden rounded border" style={{ width: THUMB_W }}>
                <div className={`overflow-hidden bg-secondary`} style={{ height: THUMB_H }}>
                  <img src={`data:${img.mime};base64,${img.base64}`} alt={img.label} className="h-full w-full object-cover" />
                </div>
                <div className={`truncate border-t px-1 py-0.5 text-center font-mono text-[7px] ${c.line} ${c.faint}`}>{img.label}</div>
                <button
                  type="button"
                  onClick={() => setImages((prev) => prev.filter((p) => p.id !== img.id))}
                  className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded bg-overlay text-white hover:bg-destructive text-destructive-foreground"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </div>
            ))}

            <label className="block shrink-0 cursor-pointer" style={{ width: THUMB_W, height: THUMB_H + 18 }}>
              <div
                className={`flex h-full flex-col items-center justify-center gap-1 rounded border-2 border-dashed transition-colors hover:border-signal hover:bg-secondary border-border`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f?.type.startsWith('image/')) readFile(f) }}
              >
                <Upload className={`h-3.5 w-3.5 ${c.faint}`} />
                <span className={`font-mono text-[7px] uppercase tracking-widest ${c.faint}`}>Upload</span>
              </div>
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => { Array.from(e.target.files ?? []).forEach(readFile); e.target.value = '' }}
              />
            </label>
          </div>
        </div>

        {result && (
          <div className={`font-mono text-[10px] px-2 py-1 border rounded ${
            result.ok
              ? 'bg-success-surface border-success/40 text-success'
              : 'bg-danger-surface border-destructive/40 text-destructive'
          }`}>
            {result.msg}
          </div>
        )}

        <div className="flex justify-end">
          <Button
            size="sm"
            className="h-7 px-3 font-mono text-[10px] uppercase tracking-widest gap-1.5"
            disabled={!isValid || sending}
            onClick={send}
          >
            <Send className="h-3 w-3" />
            {sending ? 'Sending…' : 'Send'}
          </Button>
        </div>
      </div>
    </div>
    </PageFrame>
  )
}
